import { getRouteApi, Link, useMatch } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { useLayoutEffect, useRef, useState } from 'react'
import { twJoin } from 'tailwind-merge'
import { UI_SPRING } from '@/components/shared/motion/spring.const'
import { modeIdentity } from './modeIdentity'
import { modeSwitcherSearch } from './modeSwitcherSearch'
import {
  type RegionMode,
  modeRoutePaths,
  regionModeOrder,
  useOptimisticMode,
} from './useCurrentMode'

const routeApi = getRouteApi('/regionen/$regionSlug')

const tabLayoutClassName =
  'inline-flex h-full shrink-0 items-center gap-1.5 px-3 text-sm font-medium'

/** Recessed track — reads as a window cut into the header bar. */
const modeSwitcherTrackClassName =
  'shadow-[inset_0_1px_2px_rgb(0_0_0/0.18),inset_0_1px_0_rgb(255_255_255/0.04)]'

/** Active/hover pill lifts above the recessed track. */
const modeSwitcherPillElevationClassName = 'shadow-md'

type HighlightPill = {
  left: number
  width: number
  navWidth: number
}

/**
 * Header links between region mode pages. Search params stay on the URL so map view, category
 * config, and per-mode filters survive a switch. Hidden when only the default map is available
 * or when this header is reused outside a region route.
 */
export const ModeSwitcher = () => {
  const match = useMatch({ from: '/regionen/$regionSlug', shouldThrow: false })
  if (!match) return null
  return <ModeSwitcherNav />
}

const ModeSwitcherNav = () => {
  const { regionSlug } = routeApi.useParams()
  const { availableModes } = routeApi.useLoaderData()
  const optimisticMode = useOptimisticMode()
  const [hoveredMode, setHoveredMode] = useState<RegionMode | null>(null)
  const navRef = useRef<HTMLElement>(null)
  const tabRefs = useRef<Partial<Record<RegionMode, HTMLElement | null>>>({})
  const [pill, setPill] = useState<HighlightPill | null>(null)

  const modes = regionModeOrder.filter((mode) => mode === 'map' || availableModes[mode])
  const highlightedMode = hoveredMode ?? optimisticMode

  useLayoutEffect(
    function measureHighlightedTab() {
      function measure() {
        const nav = navRef.current
        const tab = tabRefs.current[highlightedMode]
        if (!nav || !tab) return
        const navRect = nav.getBoundingClientRect()
        const tabRect = tab.getBoundingClientRect()
        const next = {
          left: tabRect.left - navRect.left,
          width: tabRect.width,
          navWidth: navRect.width,
        }
        setPill((prev) => {
          if (
            prev &&
            prev.left === next.left &&
            prev.width === next.width &&
            prev.navWidth === next.navWidth
          ) {
            return prev
          }
          return next
        })
      }

      measure()

      const nav = navRef.current
      if (!nav) return

      const observer = new ResizeObserver(measure)
      observer.observe(nav)
      for (const tab of Object.values(tabRefs.current)) {
        if (tab) observer.observe(tab)
      }

      return function disconnectTabResizeObserver() {
        observer.disconnect()
      }
    },
    [
      highlightedMode,
      availableModes.notes,
      availableModes.qa,
      availableModes.reviewLists,
      availableModes.calculator,
      availableModes.measure,
    ],
  )

  if (modes.length <= 1) return null

  const highlightedIdentity = modeIdentity[highlightedMode]
  // Compact modes (tools) are icon-only until active. The pill mirrors the same rule so both
  // copies of a tab have the same width.
  const showsLabel = (mode: RegionMode) => !modeIdentity[mode].compact || optimisticMode === mode

  return (
    <nav
      ref={navRef}
      aria-label="Modus"
      className={twJoin(
        'relative isolate inline-flex h-10 items-center rounded-md bg-gray-700 p-1',
        modeSwitcherTrackClassName,
      )}
      onMouseLeave={() => setHoveredMode(null)}
    >
      {modes.map((mode) => {
        const identity = modeIdentity[mode]
        const Icon = identity.icon
        const active = optimisticMode === mode
        return (
          <Link
            key={mode}
            ref={(node: HTMLAnchorElement | null) => {
              tabRefs.current[mode] = node
            }}
            from="/regionen/$regionSlug"
            to={modeRoutePaths[mode]}
            params={{ regionSlug }}
            search={(prev) => modeSwitcherSearch({ from: optimisticMode, to: mode }, prev)}
            className={twJoin(
              tabLayoutClassName,
              'rounded text-gray-200 outline-none focus-visible:ring-2 focus-visible:ring-white',
            )}
            aria-current={active ? 'page' : undefined}
            title={showsLabel(mode) ? undefined : identity.label}
            onMouseEnter={() => setHoveredMode(mode)}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {showsLabel(mode) ? identity.label : <span className="sr-only">{identity.label}</span>}
          </Link>
        )
      })}
      {pill && (
        <motion.div
          aria-hidden
          className={twJoin(
            'pointer-events-none absolute top-1 bottom-1 left-0 z-10 overflow-hidden',
            modeSwitcherPillElevationClassName,
          )}
          style={{ borderRadius: 4 }}
          initial={false}
          animate={{
            x: pill.left,
            width: pill.width,
            backgroundColor: highlightedIdentity.accent.hex,
          }}
          transition={UI_SPRING}
        >
          <motion.div
            className="absolute top-0 left-0 flex h-full items-center px-1"
            style={{ width: pill.navWidth }}
            initial={false}
            animate={{ x: -pill.left }}
            transition={UI_SPRING}
          >
            {modes.map((mode) => {
              const identity = modeIdentity[mode]
              const Icon = identity.icon
              return (
                <span
                  key={mode}
                  className={twJoin(tabLayoutClassName, identity.accent.invertedFgClassName)}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  {showsLabel(mode) ? identity.label : null}
                </span>
              )
            })}
          </motion.div>
        </motion.div>
      )}
    </nav>
  )
}
